import { LoadingSpinner } from "@eventr/common-storybook";
import { Stack } from "@mui/material";
import { Fragment, Suspense, lazy, useEffect, useState, type ComponentType, type ReactElement } from "react";
import { Route, matchPath } from "react-router-dom";
import { pathnameOf, type RouteMeta, type RouteNode } from "./route";

/**
 * The element tree layer. The registry (registry.ts) owns paths, params, and meta;
 * a feature's route module attaches a component (and a native bar title) to each
 * registry node here, and a single renderer turns them into <Route> elements. This
 * keeps the navigation / type-safety layer free of component imports, while
 * co-locating each screen's component with its route in the feature folder.
 *
 * The title is consumed by the native title listener (set when the view is created),
 * not rendered here.
 */
export interface RouteRender {
  path: string;
  meta: RouteMeta;
  Component: ComponentType;
  /**
   * Title shown in the native navigation bar for this view until the page sets its own. A string
   * is English used as its own `routes.*` translation key; a function returns the page's own
   * title text, so the two match. Resolved by titleForPath, not here.
   */
  title?: RouteTitle;
}

export type RouteTitle = string | (() => string);

/**
 * Attach a component (and an optional native bar title) to a registry route node.
 * The path and meta come from the node, so a route's path is still declared once.
 *
 * ```tsx
 * view(routes.account.page, AccountPage, "Account")
 * ```
 */
export function view<P extends string>(node: RouteNode<P>, Component: ComponentType, title?: RouteTitle): RouteRender {
  return { path: node.path, meta: node.meta, Component, title };
}

/**
 * Suspense fallback for a code-split route. Waits a second before rendering the spinner,
 * so a chunk that loads quickly never flashes one; fills the view so the spinner sits in
 * its true vertical centre rather than the centre of some smaller box.
 */
export function LazyFallback() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), 1000);
    return () => window.clearTimeout(id);
  }, []);

  if (!show) return null;

  return (
    <Stack sx={{ height: "100vh", justifyContent: "center", alignItems: "center" }}>
      <LoadingSpinner />
    </Stack>
  );
}

/**
 * Chunk loaders for code-split routes, keyed by route pattern, so a navigation can warm
 * the destination before it animates. See `preloadRoute`.
 */
const lazyLoaders = new Map<string, () => Promise<unknown>>();
const warmed = new Map<string, Promise<unknown>>();

/** A cold chunk should not outlast the fallback's own grace period. */
const PRELOAD_TIMEOUT_MS = 1000;

/**
 * Load a code-split screen's chunk before navigating to it.
 *
 * `lazyView` renders `LazyFallback`, which is `null` for its first second. A view
 * transition that starts while the chunk is still in flight therefore snapshots nothing as
 * its new state: the old screen slides out and nothing slides in. Warming the chunk first
 * means React commits the real screen inside the transition's update window.
 *
 * Warming has to hand `lazyView` the resolved component, not just fill the module cache:
 * `React.lazy` suspends on its first render either way, since its payload only leaves
 * `Uninitialized` a microtask after that render has already thrown.
 *
 * Resolves rather than rejects on a failed or slow load — the navigation still happens and
 * the Suspense boundary takes over, exactly as it did before.
 */
export function preloadRoute(path: string): Promise<unknown> {
  const pathname = pathnameOf(path);
  const pattern = [...lazyLoaders.keys()].find((candidate) => matchPath(candidate, pathname));
  if (!pattern) return Promise.resolve();

  const already = warmed.get(pattern);
  if (already) return already;

  const loading = lazyLoaders.get(pattern)!().catch(() => {
    // Let the retry happen through Suspense on the next visit rather than caching a failure.
    warmed.delete(pattern);
  });

  warmed.set(pattern, loading);
  return Promise.race([loading, new Promise((resolve) => setTimeout(resolve, PRELOAD_TIMEOUT_MS))]);
}

/**
 * Attach a code-split component to a registry node.
 *
 * Same contract as `view`, but the screen loads on demand instead of riding in the entry
 * chunk.
 *
 * The Suspense boundary lives here rather than in `renderRoutes` so that only routes which
 * opt in get one, and a screen's chunk loading cannot suspend anything above it — under
 * native navigation each pushed view portals its own copy of the subtree, so the fallback
 * needs to land inside that view.
 *
 * ```tsx
 * lazyView(routes.team.member, () => import("./members/team-member").then((m) => ({ default: m.TeamMemberPage })))
 * ```
 */
export function lazyView<P extends string>(
  node: RouteNode<P>,
  load: () => Promise<{ default: ComponentType }>,
  title?: RouteTitle,
): RouteRender {
  // Set by preloadRoute, and read at render time: warming always finishes before the
  // navigation it precedes, so the screen's first render already sees it.
  let Warm: ComponentType | undefined;

  lazyLoaders.set(node.path, () => load().then((module) => (Warm = module.default)));

  const Screen = lazy(load);
  // The boundary stays even once warm, so a screen that suspends on its own still has one.
  const Suspended = () => <Suspense fallback={<LazyFallback />}>{Warm ? <Warm /> : <Screen />}</Suspense>;

  return view(node, Suspended, title);
}

/**
 * Render a feature's route list as <Route> elements. Routes are flat with absolute
 * paths, so this is dropped straight into the app's <Routes> tree (React Router
 * flattens the returned fragment).
 */
export function renderRoutes(defs: RouteRender[]): ReactElement {
  return (
    <Fragment>
      {defs.map((def) => {
        const { Component } = def;
        return <Route key={def.path} path={def.path} element={<Component />} />;
      })}
    </Fragment>
  );
}
